import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
} from "../ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { RefreshCw, Check } from "lucide-react";
import { useContextDetection } from "../../hooks/useContextDetection";
import { UnifiedUserService } from "../../services/unifiedServices";
import { BusinessUnitService } from "../../services/businessUnitService";
import { getApiUrl } from "../../config/api";
import AddUserToOrganization from "../organizations/AddUserToOrganization";
import AddUserToBusinessUnitUnified from "../BusinessUnit/AddUserToBusinessUnitUnified";
import InviteManagement from "../Invites/InviteManagement";
import UserTable from "../Users/UserTable";
import UserStats from "../Users/UserStats";
import PageHeader from "../Common/PageHeader";

const UnifiedUsersPage = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const {
    contextId,
    contextType,
    organizationId,
    businessUnitId,
    isOrganizationContext,
    canManage,
  } = useContextDetection();

  const [contextData, setContextData] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Get context-specific data (organization or business unit details)
  const fetchContextData = async () => {
    try {
      if (isOrganizationContext && organizationId) {
        // Fetch organization details
        const response = await fetch(
          getApiUrl(`organizations/${organizationId}`),
          {
            headers: {
              Authorization: `Bearer ${await getAccessTokenSilently()}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setContextData(data);
        }
      } else if (!isOrganizationContext && businessUnitId) {
        // Fetch business unit details
        const data = await BusinessUnitService.getBusinessUnitDetails(
          businessUnitId,
          getAccessTokenSilently
        );
        setContextData(data);
      }
    } catch (err) {
      console.error("Error fetching context data:", err);
    }
  };

  // Fetch business units for organization context
  const fetchBusinessUnits = async () => {
    if (isOrganizationContext && organizationId) {
      try {
        const data = await BusinessUnitService.getBusinessUnitsForOrganization(
          organizationId,
          getAccessTokenSilently
        );
        setBusinessUnits(data);
      } catch (err) {
        console.error("Error fetching business units:", err);
      }
    }
  };

  const fetchUsers = async () => {
    if (!contextId) return;

    try {
      setLoading(true);
      const data = await UnifiedUserService.getUsers(
        contextType,
        contextId,
        organizationId || contextData?.organization_id,
        getAccessTokenSilently
      );
      setUsers(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (contextId) {
      fetchContextData();
      fetchBusinessUnits();
    }
  }, [contextId, isOrganizationContext]);

  useEffect(() => {
    if (contextId && (isOrganizationContext || contextData)) {
      fetchUsers();
    }
  }, [contextId, isOrganizationContext, contextData]);

  const handleUserAdded = () => {
    fetchUsers();
  };

  const handleRemoveUser = async (userId, businessUnitId, roleType) => {
    try {
      setActionLoading(`remove-${userId}-${businessUnitId}-${roleType}`);
      setError(null);

      await UnifiedUserService.removeUser(
        contextType,
        businessUnitId || contextId,
        userId,
        roleType,
        getAccessTokenSilently
      );

      setSuccess("User removed successfully");
      fetchUsers();
      setConfirmDialog(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Generate context-aware page title
  const getPageTitle = () => {
    if (isOrganizationContext) {
      return "Manage Users";
    } else {
      return "Manage Users";
    }
  };

  const getPageSubtitle = () => {
    if (isOrganizationContext) {
      return "View and manage all users across your organization";
    } else {
      return "View and manage all users in this business unit";
    }
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-3 flex-wrap">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-36" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Table Skeleton */}
        <SkeletonCard>
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <SkeletonTable rows={8} columns={6} />
          </div>
        </SkeletonCard>
      </div>
    );
  }

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <PageHeader
          pageName="Users & Access"
          entityName={contextData?.name}
          entityType={isOrganizationContext ? "organization" : "business unit"}
        />
        <div className="flex gap-3 flex-wrap">
          {isOrganizationContext ? (
            <AddUserToOrganization
              organizationId={organizationId}
              businessUnits={businessUnits}
              onUserAdded={handleUserAdded}
            />
          ) : (
            <AddUserToBusinessUnitUnified
              businessUnitId={businessUnitId}
              onUserAdded={handleUserAdded}
            />
          )}

          <InviteManagement
            organizationId={organizationId || contextData?.organization_id}
            businessUnitId={isOrganizationContext ? null : businessUnitId}
            onInviteUpdate={handleUserAdded}
          />

          <Button
            variant="outline"
            size="sm"
            onClick={fetchUsers}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <Alert className="mb-6" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6">
          <Check className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* User Stats */}
      <UserStats users={users} contextType={contextType} />

      {/* Users Table */}
      <UserTable
        users={users}
        contextType={contextType}
        businessUnits={businessUnits}
        canManage={canManage}
        onRemoveUser={handleRemoveUser}
        onRefresh={fetchUsers}
        onAddUser={() => {
          // This will be handled by the buttons in the header
        }}
        onInviteManagement={() => {
          // This will be handled by the buttons in the header
        }}
      />

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog
          open={!!confirmDialog}
          onOpenChange={() => setConfirmDialog(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm User Removal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                Are you sure you want to remove{" "}
                <strong>{confirmDialog.user.name}</strong> as a{" "}
                <strong>{confirmDialog.roleDisplay}</strong>{" "}
                {contextType === "organization" && (
                  <>
                    from <strong>{confirmDialog.businessUnit.name}</strong>
                  </>
                )}
                ?
              </p>
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. The user will lose access to all
                resources associated with this role.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmDialog(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    handleRemoveUser(
                      confirmDialog.user.id,
                      confirmDialog.businessUnit?.id || contextId,
                      confirmDialog.role
                    )
                  }
                  disabled={actionLoading}
                >
                  {actionLoading ? <Spinner className="h-4 w-4 mr-2" /> : null}
                  Remove User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default UnifiedUsersPage;
